/* ==========================================================================
   TØTE — Splide rails (Kopstorm CommV)
   assets/kv-rails.js

   Turns two horizontally overflowing lists into one row Splide sliders with
   arrows, so nothing runs off screen without an affordance:

     .kv-r3-rail                    offer tiles from the R3 layer
     .related-products              the colour and model thumbnails rendered
                                    by the related_products and
                                    more_related_products blocks in
                                    main-product, which wrap to four rows on
                                    a bag with fourteen colours
     .kv-variant-tabs-content-in    the same grid inside the kv variant
                                    selector, which ships hidden on most
                                    templates but is used on some

   Both keep working without JS: the CSS falls back to a scrollable single
   row, so the page never shows a wrapped grid again either way.

   Mounting Splide inside a hidden container gives zero width slides, so a
   rail only mounts once it is actually visible. That matters here because
   the R3 blocks ship display:none until they are placed, and the variant
   tabs hide every panel but the active one.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__kvRailsInit) return;
  window.__kvRailsInit = true;

  var OFFER_OPTS = {
    autoWidth: true,
    arrows: true,
    pagination: false,
    gap: '6px',
    /* omitEnd clamps the index at the trimmed end, so pressing prev there
       decrements an index that resolves to the same scroll position and the
       arrow looks dead. trimSpace 'move' trims the trailing gap the same way
       but keeps prev stepping one slide at a time. */
    trimSpace: 'move',
    speed: 400,
    dragMinThreshold: { mouse: 4, touch: 10 }
  };

  var VARIANT_OPTS = {
    autoWidth: true,
    arrows: true,
    pagination: false,
    gap: '6px',
    /* omitEnd clamps the index at the trimmed end, so pressing prev there
       decrements an index that resolves to the same scroll position and the
       arrow looks dead. trimSpace 'move' trims the trailing gap the same way
       but keeps prev stepping one slide at a time. */
    trimSpace: 'move',
    speed: 400,
    dragMinThreshold: { mouse: 4, touch: 10 }
  };

  function visible(el) {
    return !!el && (el.offsetParent !== null || el.getClientRects().length > 0);
  }

  /* Wrap an existing list in the Splide skeleton without moving it out of
     its parent, so any theme CSS keyed on the parent still applies. */
  function build(list, slideSelector, modifier) {
    if (list.dataset.kvRail === '1') return null;
    var parent = list.parentNode;
    if (!parent) return null;

    var root = document.createElement('div');
    root.className = 'splide kv-rail ' + modifier;
    var track = document.createElement('div');
    track.className = 'splide__track';

    parent.insertBefore(root, list);
    root.appendChild(track);
    track.appendChild(list);

    list.classList.add('splide__list');
    Array.prototype.forEach.call(list.querySelectorAll(':scope > ' + slideSelector), function (child) {
      child.classList.add('splide__slide');
    });

    list.dataset.kvRail = '1';
    return root;
  }

  function mount(root, opts) {
    if (!root || root.__kvSplide || typeof window.Splide !== 'function') return;
    try {
      root.__kvSplide = new window.Splide(root, opts).mount();
    } catch (e) {
      /* leave the CSS fallback in place */
    }
  }

  function whenVisible(root, opts) {
    if (!root) return;
    if (visible(root)) { mount(root, opts); return; }
    if (!('ResizeObserver' in window)) { mount(root, opts); return; }
    var ro = new ResizeObserver(function () {
      if (visible(root)) { ro.disconnect(); mount(root, opts); }
    });
    ro.observe(root);
  }

  /* ---- offer rails ------------------------------------------------------ */
  function offerRails() {
    Array.prototype.forEach.call(document.querySelectorAll('.kv-r3-rail'), function (list) {
      var root = build(list, 'li', 'kv-rail--offer');
      whenVisible(root || list.closest('.kv-rail'), OFFER_OPTS);
    });
  }

  /* ---- variant thumbnails ----------------------------------------------- */
  /* Two containers hold the same kind of grid. .related-products is what the
     live product templates actually render; .kv-variant-tabs-content-in is
     the kv variant selector, which ships display:none on most templates. */
  var VARIANT_SELECTORS = '.related-products, .kv-variant-tabs-content-in';

  function variantRails() {
    Array.prototype.forEach.call(document.querySelectorAll(VARIANT_SELECTORS), function (list) {
      if (list.dataset.kvRail === '1') return;
      var slides = list.querySelectorAll(':scope > a');
      if (slides.length < 2) { list.dataset.kvRail = '1'; return; }

      var root = build(list, 'a', 'kv-rail--variant');
      if (!root) return;

      /* Tell the shopper the row keeps going. */
      var label = document.createElement('p');
      label.className = 'kv-rail__count';
      label.textContent = slides.length + (slides.length === 1 ? ' colour' : ' colours');
      root.parentNode.insertBefore(label, root);

      whenVisible(root, VARIANT_OPTS);
    });
  }

  /* :has() fallback for older browsers: tag the custom_liquid wrappers that
     hold the low stock and in stock badges so the CSS can zero their bottom
     margins. */
  function tagLowStock() {
    Array.prototype.forEach.call(document.querySelectorAll('.custom-liquid .low-stock-box'), function (box) {
      var wrap = box.closest('.custom-liquid');
      if (wrap) wrap.classList.add('kv-has-lowstock');
    });
    Array.prototype.forEach.call(document.querySelectorAll('.custom-liquid .in-stock-box'), function (box) {
      var wrap = box.closest('.custom-liquid');
      if (wrap) wrap.classList.add('kv-has-instock');
    });
  }

  /* T65 gate. The variant slider and the tightened PDP rhythm run as a split
     test, so they only apply once kv-tests-r2.js has mirrored the revealed
     marker onto html (or Intelligems has set the class itself). The offer
     rails are ungated: they live inside R3 blocks, which carry their own
     gate, and control never sees them. */
  function t65() {
    return document.documentElement.classList.contains('kv-t65');
  }

  function boot() {
    offerRails();
    /* The stock badge spacing is permanent since 20 Aug 2026, so the :has()
       fallback classes are tagged for every visitor, not only inside T65. */
    tagLowStock();
    if (!t65()) return;
    variantRails();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  window.addEventListener('load', boot);
  document.addEventListener('shopify:section:load', boot);

  /* The R3 blocks are moved into the page after their own boot, and the
     variant tabs swap panels, so refresh on both. */
  document.addEventListener('kv:r3:placed', boot);
  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('.kv-tab')) setTimeout(boot, 60);
  });

  /* Intelligems can add kv-t65 at any point after the first paint. */
  if (window.MutationObserver) {
    new MutationObserver(function () { if (t65()) boot(); })
      .observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  }
})();
